import type { IFileItem } from '../models/FileItem';
import type { IFolderItem } from '../models/FolderItem';
import type { IOrganizationPlan, IPlannedChange } from '../models/Plan';
import type { IScanResult } from '../models/ScanResult';
import { getSystemFileSource } from '../rules/FilenameRule';
import { isCatchAllFolderName, normalizeFolderName } from '../rules/FolderRules';
import { fileBaseName, values } from '../rules/ruleUtils';
import { quarantineStamp, QUARANTINE_FOLDER } from '../plan/PlanGenerator';
import type { ITagSet } from '../tagging/Tagging';

export interface IStructureTemplate {
  id: string;
  name: string;
  description: string;
  categories: string[];
}

export const STRUCTURE_TEMPLATES: IStructureTemplate[] = [
  {
    id: 'small-business', name: 'Small business', description: 'Most companies: clients, work, money, people.',
    categories: ['Clients', 'Projects', 'Finance', 'HR', 'Marketing', 'Operations', 'Legal']
  },
  {
    id: 'consultancy', name: 'Consultancy / agency', description: 'Client work, proposals and internal material.',
    categories: ['Clients', 'Projects', 'Proposals', 'Finance', 'Internal', 'Templates']
  },
  {
    id: 'nonprofit', name: 'Nonprofit', description: 'Programs, funding and governance.',
    categories: ['Programs', 'Donors & Grants', 'Finance', 'HR', 'Board', 'Communications', 'Operations']
  }
];

/** Words that point to each well-known category. Custom categories match on their own name. */
const KEYWORDS: { [category: string]: string[] } = {
  clients: ['client', 'customer', 'account', 'corporation', 'corp', 'ltd', 'inc', 'llc'],
  projects: ['project', 'website', 'redesign', 'initiative', 'deliverable', 'round', 'feedback'],
  finance: ['finance', 'financial', 'invoice', 'budget', 'accounting', 'expense', 'tax', 'receipt', 'purchase', 'payment', 'billing'],
  hr: ['hr', 'human', 'resource', 'handbook', 'pto', 'leave', 'onboarding', 'benefit', 'employee', 'staff', 'recruitment', 'hiring', 'payroll', 'org', 'chart', 'policy'],
  marketing: ['marketing', 'brochure', 'campaign', 'brand', 'social', 'newsletter', 'logo', 'press', 'launch', 'advert'],
  operations: ['operation', 'ops', 'process', 'procedure', 'admin', 'administration', 'facility', 'vendor', 'supplier', 'inventory', 'equipment'],
  legal: ['legal', 'contract', 'agreement', 'nda', 'terms', 'compliance', 'license', 'licence', 'insurance'],
  proposals: ['proposal', 'quote', 'quotation', 'bid', 'tender', 'rfp', 'pitch', 'estimate'],
  internal: ['internal', 'team', 'meeting', 'minutes', 'notes', 'note', 'planning', 'kickoff'],
  templates: ['template', 'boilerplate', 'form'],
  programs: ['program', 'programme', 'service', 'event', 'workshop', 'outreach'],
  'donors & grants': ['donor', 'grant', 'fundraising', 'donation', 'sponsor', 'funding'],
  board: ['board', 'governance', 'trustee', 'bylaw', 'agenda'],
  communications: ['communication', 'newsletter', 'press', 'media', 'social', 'website', 'brochure', 'announcement']
};

const STOP: { [w: string]: boolean } = {
  the: true, and: true, of: true, for: true, new: true, copy: true, final: true, document: true, documents: true,
  doc: true, docs: true, file: true, files: true, folder: true, microsoft: true, word: true, untitled: true
};

/** Folder-name matches count more than file-name matches. */
const FOLDER_NAME_WEIGHT: number = 3;
const FILE_NAME_WEIGHT: number = 2;
const TAG_WEIGHT: number = 2;
/** Below this, an item is left for the admin to place. */
const MIN_SCORE: number = 1.5;
/** How many file names inside a folder are sampled when judging the folder. */
const SAMPLE_FILES: number = 50;

function tokens(text: string): string[] {
  return text.toLowerCase().split(/[^a-z]+/)
    .filter(w => w.length >= 2 && !STOP[w])
    .map(w => (w.length > 3 && /s$/.test(w) && !/ss$/.test(w) ? w.slice(0, -1) : w));
}

function keywordsFor(category: string): string[] {
  const own: string[] = tokens(category);
  return own.concat(KEYWORDS[category.toLowerCase()] ?? []);
}

function join(parent: string, name: string): string {
  return parent ? `${parent}/${name}` : name;
}

function filesUnder(scan: IScanResult, folder: IFolderItem, out: IFileItem[] = []): IFileItem[] {
  for (const id of folder.fileIds) {
    if (out.length >= SAMPLE_FILES) { return out; }
    out.push(scan.files[id]);
  }
  for (const id of folder.childFolderIds) {
    filesUnder(scan, scan.folders[id], out);
  }
  return out;
}

export interface IStructureOptions {
  /** Folder to organize: '' for the library root. */
  basePath: string;
  /** Category names, e.g. from a template, possibly edited. */
  categories: string[];
  /** Tags already on files (from the Tags feature), when available. */
  tags?: { [fileId: string]: ITagSet };
}

export interface IStructureProposal {
  plan: IOrganizationPlan;
  /** Existing folders that already are a category and stay where they are. */
  keptFolders: string[];
  /** Destination folder path for each category. */
  categoryPaths: { [category: string]: string };
}

/**
 * Proposes a structure of a few top-level categories inside `basePath`. Existing folders move
 * as a whole into the best-matching category (judged by their name and the names of the
 * files inside); a folder that already is a category stays and is used as that category.
 * Loose files, and the contents of catch-all folders (Misc, Old…), are placed one by one,
 * using tags where they exist (Organization → Clients/<org>, Project → Projects/<project>).
 * Anything without a clear match is left for the admin to place.
 */
export function proposeStructure(scan: IScanResult, options: IStructureOptions): IStructureProposal {
  const base: IFolderItem | undefined = options.basePath
    ? values(scan.folders).filter(f => f.path.toLowerCase() === options.basePath.toLowerCase())[0]
    : scan.folders[scan.rootId];
  if (!base) {
    throw new Error(`No folder named "${options.basePath}" exists in this library.`);
  }
  const categories: string[] = options.categories.map(c => c.trim()).filter(c => !!c);
  const children: IFolderItem[] = base.childFolderIds.map(id => scan.folders[id]);

  // A category is "already there" if a child folder has its name — or means the same thing
  // (HR / Human Resources). The exact-name folder wins; the others move into it.
  const categoryPaths: { [category: string]: string } = {};
  const keptIds: { [id: string]: boolean } = {};
  for (const category of categories) {
    const same: IFolderItem[] = children.filter(f => normalizeFolderName(f.name) === normalizeFolderName(category));
    const exact: IFolderItem | undefined = same.filter(f => f.name.toLowerCase() === category.toLowerCase())[0] ?? same[0];
    if (exact) {
      keptIds[exact.id] = true;
      categoryPaths[category] = exact.path;
    } else {
      categoryPaths[category] = join(base.path, category);
    }
  }

  // What to place: base's folders (unless kept), base's loose files, and — recursively —
  // the contents of catch-all folders, which are never moved as a whole.
  const folders: IFolderItem[] = [];
  const files: IFileItem[] = [];
  const collect = (folder: IFolderItem): void => {
    for (const id of folder.fileIds) {
      if (!getSystemFileSource(scan.files[id])) {
        files.push(scan.files[id]);
      }
    }
    for (const id of folder.childFolderIds) {
      const child: IFolderItem = scan.folders[id];
      if (keptIds[id] || scan.protectedFolders[id]) {
        continue;
      }
      if (isCatchAllFolderName(child.name)) {
        collect(child);
      } else {
        folders.push(child);
      }
    }
  };
  collect(base);

  const score = (words: { text: string; weight: number }[]): { category: string; score: number; matched: string[] } | undefined => {
    let best: { category: string; score: number; matched: string[] } | undefined;
    for (const category of categories) {
      const keywords: string[] = keywordsFor(category);
      let total: number = 0;
      const matched: string[] = [];
      for (const w of words) {
        const found: string[] = tokens(w.text).filter(t => keywords.indexOf(t) !== -1);
        if (found.length > 0) {
          total += w.weight * found.length;
          matched.push(...found.filter(f => matched.indexOf(f) === -1));
        }
      }
      if (!best || total > best.score) {
        best = { category, score: total, matched };
      }
    }
    return best && best.score >= MIN_SCORE ? best : undefined;
  };

  const confidence = (s: number): number => Math.min(0.85, Math.round((0.4 + s / 10) * 100) / 100);
  const changes: IPlannedChange[] = [];

  for (const folder of folders) {
    const sample: IFileItem[] = filesUnder(scan, folder);
    const perFile: number = sample.length > 0 ? 1 / sample.length : 0;
    const best = score([{ text: folder.name, weight: FOLDER_NAME_WEIGHT }]
      .concat(sample.map(f => ({ text: fileBaseName(f), weight: FILE_NAME_WEIGHT * perFile * 2 }))));
    changes.push({
      id: `restructure:${folder.id}`, type: 'restructure', itemKind: 'folder', targetId: folder.id,
      suggestedParentPath: best ? categoryPaths[best.category] : undefined,
      needsInput: !best,
      reason: best
        ? `Moves as a whole, with everything inside, into ${best.category}: its name and files mention "${best.matched.slice(0, 4).join('", "')}".`
        : `No category clearly fits this folder. Choose one of: ${categories.join(', ')}.`,
      confidence: best ? confidence(best.score) : 0,
      issueIds: []
    });
  }

  const clients: string | undefined = categories.filter(c => c.toLowerCase() === 'clients')[0];
  const projects: string | undefined = categories.filter(c => c.toLowerCase() === 'projects')[0];
  for (const file of files) {
    const tags: ITagSet = options.tags?.[file.id] ?? {};
    let parentPath: string | undefined;
    let reason: string = '';
    let conf: number = 0;
    if (clients && tags.organization) {
      parentPath = join(categoryPaths[clients], tags.organization.replace(/["*:<>?/\\|]/g, ' ').trim());
      reason = `Tagged with the organization "${tags.organization}".`;
      conf = 0.8;
    } else if (projects && tags.project) {
      parentPath = join(categoryPaths[projects], tags.project.replace(/["*:<>?/\\|]/g, ' ').trim());
      reason = `Tagged with the project "${tags.project}".`;
      conf = 0.8;
    } else {
      const words: { text: string; weight: number }[] = [{ text: fileBaseName(file), weight: FILE_NAME_WEIGHT }];
      if (tags.documentType && tags.documentType !== 'Other') {
        words.push({ text: tags.documentType, weight: TAG_WEIGHT });
      }
      const best = score(words);
      if (best) {
        parentPath = categoryPaths[best.category];
        reason = `Its name${tags.documentType ? ' and Document Type' : ''} mention "${best.matched.join('", "')}".`;
        conf = confidence(best.score);
      }
    }
    changes.push({
      id: `restructure:${file.id}`, type: 'restructure', itemKind: 'file', targetId: file.id,
      suggestedParentPath: parentPath,
      needsInput: !parentPath,
      reason: parentPath ? reason : `No category clearly fits this file. Choose one of: ${categories.join(', ')}.`,
      confidence: conf,
      issueIds: []
    });
  }

  return {
    plan: { changes, quarantinePath: `${QUARANTINE_FOLDER}/${quarantineStamp(scan.scannedAt)}` },
    keptFolders: Object.keys(keptIds).map(id => scan.folders[id].path),
    categoryPaths
  };
}
